import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { trackRecommendedTripsLoaded } from 'utils/userPreferences';
import { LoaderPulse, Spacing } from '@reservamos/elements';
import { useSelector } from 'react-redux';
import useExperimentalFeature from 'hooks/useExperimentalFeature';
import shouldShowWelcomeDigitalDiscount from 'utils/welcomeDigitalDiscountValidation';
import useRecommendedTrips from '../../../context/RecommendedTrips/useRecommendedTrips';
import TripList from '../TripList/TripList';
import '../../../styles/components/RecommendedTrips';
import ResultsBannerSlot, { resolveRecommendedTripsBanner } from './ResultsBannerSlot';

/**
 * Renders a list of recommended trips.
 *
 * @param {Object} props - way, providerId, selectTrip, onOptionClick, filtersApplied, departureTrip, couponCode, discountedTripsByCoupon, searchTrips, isRoundTrip, isExchange, originCityName, destinationCityName, specialDiscountVariant, showSpecialDiscountBanner.
 * @param {string} props.way - The way of the trip.
 * @param {string} props.providerId - The provider ID.
 * @param {function} props.selectTrip - The function to select a trip.
 * @param {function} props.onOptionClick - The function to handle option click.
 * @param {Array} props.filtersApplied - The applied filters.
 * @param {Object} props.departureTrip - The departure trip selected.
 * @param {string} [props.couponCode] - Applied coupon code for discounted trips.
 * @param {Array} [props.discountedTripsByCoupon] - Trip IDs eligible for the coupon discount.
 * @param {Array} props.searchTrips - All search result trips for quick purchase visibility.
 * @param {boolean} props.isRoundTrip - Whether the search is round trip.
 * @param {boolean} props.isExchange - Whether the current flow is an exchange.
 * @param {Function} props.onBannerClick - Called when the quick purchase banner is clicked.
 * @param {string} [props.originCityName] - Origin city name for welcome digital discount validation.
 * @param {string} [props.destinationCityName] - Destination city name for welcome digital discount validation.
 * @param {'etn'|'gho'|'costa'|'gfa'|'rochoa'} props.specialDiscountVariant - Brand variant for the special discount banner.
 * @param {boolean} [props.showSpecialDiscountBanner] - Whether to show the special discount banner slot.
 *
 * @returns {JSX.Element|null} The rendered component.
 */
const RecommendedTripsList = ({
  way,
  providerId,
  selectTrip,
  onOptionClick,
  filtersApplied = [],
  departureTrip,
  couponCode,
  discountedTripsByCoupon,
  searchTrips,
  isRoundTrip,
  isExchange,
  originCityName,
  destinationCityName,
  specialDiscountVariant,
  showSpecialDiscountBanner,
  onQuickPurchaseRequest,
  onBannerClick,
}) => {
  const showRecommendedEventSent = useRef();
  const {
    trips: originalTrips,
    isLoading,
    requested,
  } = useRecommendedTrips({
    providerId,
    way,
    departureTripArrival: !departureTrip?.openTicket && departureTrip?.arrival,
    departureTransporterName: departureTrip?.line?.transporterName,
  });

  const { passengers: searchPassengers } = useSelector((state) => state.search?.toJS() || {});
  const quickPurchaseEnabled = useExperimentalFeature('QUICK_PURCHASE_ENABLED');

  const showWelcomeDigitalDiscountBanner = shouldShowWelcomeDigitalDiscount({
    originCityName,
    destinationCityName,
  });

  // Filter out trips that don't have enough seats available for the requested passengers
  const trips = originalTrips.filter((trip) => {
    if (!trip.passengerTypes || !searchPassengers) return true;

    // Verify that each passenger type has sufficient availability
    // for the number of passengers requested in the search
    return trip.passengerTypes.every((passengerType) => {
      const requiredQuantity = searchPassengers[passengerType.type] || 0;
      // Treat negative availability as 0 (unavailable)
      const actualAvailability = Math.max(0, passengerType.availability);
      return actualAvailability >= requiredQuantity;
    });
  });

  if (isLoading && !trips.length) {
    return <LoaderPulse color="accent" hasWrapper={false} />;
  }

  if (requested && !showRecommendedEventSent.current) {
    trackRecommendedTripsLoaded({
      way,
      recommendationsTypes: trips?.map((trip) => trip.recommendationType),
    });
    showRecommendedEventSent.current = true;
  }

  if (!trips.length) {
    if (
      resolveRecommendedTripsBanner(
        searchTrips,
        showWelcomeDigitalDiscountBanner,
        quickPurchaseEnabled,
        showSpecialDiscountBanner,
      ) === 'none'
    ) {
      return null;
    }

    return (
      <Spacing vertical>
        <ResultsBannerSlot
          searchTrips={searchTrips}
          isRoundTrip={isRoundTrip}
          isExchange={isExchange}
          quickPurchaseEnabled={quickPurchaseEnabled}
          showWelcomeDigitalDiscountBanner={showWelcomeDigitalDiscountBanner}
          showSpecialDiscountBanner={showSpecialDiscountBanner}
          specialDiscountVariant={specialDiscountVariant}
          variant="recommendedTripsEmpty"
          onBannerClick={onBannerClick}
        />
      </Spacing>
    );
  }

  const horizontalMode = trips.length > 1;

  return (
    <div className="recommended-trips-container">
      <ResultsBannerSlot
        searchTrips={searchTrips}
        isRoundTrip={isRoundTrip}
        isExchange={isExchange}
        quickPurchaseEnabled={quickPurchaseEnabled}
        showWelcomeDigitalDiscountBanner={showWelcomeDigitalDiscountBanner}
        showSpecialDiscountBanner={showSpecialDiscountBanner}
        specialDiscountVariant={specialDiscountVariant}
        variant="recommendedTripsHeaderMobile"
        onBannerClick={onBannerClick}
      />
      <Spacing vertical>
        <ResultsBannerSlot
          searchTrips={searchTrips}
          isRoundTrip={isRoundTrip}
          isExchange={isExchange}
          quickPurchaseEnabled={quickPurchaseEnabled}
          showWelcomeDigitalDiscountBanner={showWelcomeDigitalDiscountBanner}
          showSpecialDiscountBanner={showSpecialDiscountBanner}
          specialDiscountVariant={specialDiscountVariant}
          variant="recommendedTripsHeaderAside"
          onBannerClick={onBannerClick}
        />
        <TripList
          trips={trips}
          way={way}
          selectTrip={selectTrip}
          onOptionClick={onOptionClick}
          filtersApplied={filtersApplied}
          isNotFiltered
          highLightAsMobile={horizontalMode}
          isHorizontal={horizontalMode}
          departureTrip={departureTrip}
          roundTrip={Boolean(isRoundTrip)}
          isRecommendedList
          couponCode={couponCode}
          discountedTripsByCoupon={discountedTripsByCoupon}
          onQuickPurchaseRequest={onQuickPurchaseRequest}
        />
      </Spacing>
    </div>
  );
};

RecommendedTripsList.propTypes = {
  way: PropTypes.string.isRequired,
  providerId: PropTypes.string.isRequired,
  selectTrip: PropTypes.func,
  onOptionClick: PropTypes.func,
  filtersApplied: PropTypes.array,
  departureTrip: PropTypes.object,
  couponCode: PropTypes.string,
  discountedTripsByCoupon: PropTypes.array,
  searchTrips: PropTypes.array,
  isRoundTrip: PropTypes.bool,
  isExchange: PropTypes.bool,
  originCityName: PropTypes.string,
  destinationCityName: PropTypes.string,
  onQuickPurchaseRequest: PropTypes.func,
  onBannerClick: PropTypes.func,
  specialDiscountVariant: PropTypes.oneOf(['etn', 'gho', 'costa', 'gfa', 'rochoa']),
  showSpecialDiscountBanner: PropTypes.bool,
};

RecommendedTripsList.defaultProps = {
  showSpecialDiscountBanner: false,
};

export default RecommendedTripsList;
